import React, { useContext, useEffect, useState } from 'react'
import { TransactionDto } from '@/api/Transaction/Types/Transaction'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { ViewTabs } from '@/components/ui/tabs'
import { links } from '@/utils/featureToggles'
import { UpdateBonusTransaction } from 'api/Transaction/Types/UpdateBonusTransaction'
import { Modal } from 'components/CustomModal'
import { WarningOutlined } from 'components/Icon'
import { isEmpty } from 'lodash'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchBonusTransactionStatusesAction,
  fetchBonusTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { fetchBonusLabelsAction } from 'store/wallet/wallet-actions'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { getCreditCreditOrFirstOneWallet } from 'utils/wallet'

import ClientAreaAuditLogModal from '../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { AggregateDataTable } from '../AggregateDataTable'
import { TransactionDetailsTab } from '../DetailTabs'
import { LinkedTransactionTable } from '../LinkedTransactions'
import { PaymentProviderTransactionTable } from '../PaymentProviderTransactionTable'
import { TransactionDetailsLotsProfitStatistics } from '../TransactionDetailsLotsProfitStatistics/TransactionDetailsLotsProfitStatistics'
import TransactionDetailsNotesPage from '../TransactionDetailsNotes/TransactionDetailsNotesPage'
import TransactionHistoryDetails from '../TransactionHistoryDetails'
import { TransactionsStatisticOverview } from '../TransactionsStatisticOverview/TransactionsStatisticOverview'
import { TransactionStatusHeaderAction } from '../TransactionStatusHeaderAction'
import { TransactionDetailsBonusGeneralPage } from './TransactionDetailsBonusGeneralPage'

interface Props {
  data: TransactionDto
}

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const walletsApiClient = useApiClient(WalletsApiClient)

  return {
    fetchTransactionBonusStatuses: async () =>
      dispatch(fetchBonusTransactionStatusesAction(walletsApiClient)),
    fetchTransactionBonusTypes: async () =>
      dispatch(fetchBonusTransactionTypeAction(walletsApiClient)),
    fetchCampaignBonusLabels: async () => dispatch(fetchBonusLabelsAction(walletsApiClient)),
  }
}

const TransactionDetailsBonusPage: React.FC<Props> = ({ data }) => {
  const [auth] = useContext(AuthSessionContext)

  const walletsApiClient = useApiClient(WalletsApiClient)

  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false)
  const [client, setClient] = useState<{ id: string; name: string } | undefined>(undefined)
  const [transactionWalletId, setTransactionWalletId] = useState<string | undefined>()

  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<string>(TransactionDetailsTab.GENERAL)
  const [transaction, setTransaction] = useState(data)
  useEffect(() => {
    setTransaction(data)
  }, [data])

  useEffect(() => {
    if (data) {
      if (data.tradingAccountTransactions && data.tradingAccountTransactions.length > 0) {
        const tradingAccountTransaction = data.tradingAccountTransactions[0]
        if (tradingAccountTransaction.client) {
          setClient(tradingAccountTransaction.client)
          setTransactionWalletId(tradingAccountTransaction.wallet?.id)
        }
      } else {
        const transactionWallet = getCreditCreditOrFirstOneWallet(data.walletTransactions)?.wallet
        if (transactionWallet) {
          setClient(transactionWallet.client)
          setTransactionWalletId(transactionWallet.id)
        }
      }
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const permissions = auth?.permissions || []

  const { fetchTransactionBonusTypes, fetchTransactionBonusStatuses, fetchCampaignBonusLabels } =
    useMapDispatchToProps()

  const bonusTypes = useAppSelector((state) => state.transactions.bonusTypes)
  const bonusStatuses = useAppSelector((state) => state.transactions.bonusStatuses)
  const bonusLabels = useAppSelector((state) => state.wallet.bonusLabels)

  useEffect(() => {
    if (isEmpty(bonusTypes)) fetchTransactionBonusTypes()
    if (isEmpty(bonusStatuses)) fetchTransactionBonusStatuses()
    if (isEmpty(bonusLabels)) fetchCampaignBonusLabels()
  }, [])

  const onSubmit = async (data: UpdateBonusTransaction, id: string): Promise<void> => {
    await walletsApiClient.updateBonusTransaction(data, id)

    const updated = await walletsApiClient.getTransactionById(id)
    setTransaction(updated)
  }

  return (
    <React.Fragment>
      {data && client && (
        <React.Fragment>
          {auditLogVisible && (
            <ClientAreaAuditLogModal
              isVisible={auditLogVisible}
              onClose={() => setAuditLogVisible(false)}
              transactionId={data.id}
            />
          )}
          {showHistoryModal && (
            <Modal
              width={400}
              visible={showHistoryModal}
              onClose={() => setShowHistoryModal(false)}
            >
              <TransactionHistoryDetails id={data.id} />
            </Modal>
          )}
          <Breadcrumbs
            crumbs={[
              {
                title: 'Transaction - Bonus',
                link: links.transactionBonus.url,
              },
              { title: data.referenceId, link: '/transactions/' + data.id },
            ]}
          />
          <NavigationHeader>
            <span>{data.referenceId}</span>
            <TransactionStatusHeaderAction
              statusName={data.state.name}
              onStatusClick={() => setShowHistoryModal(true)}
            />
          </NavigationHeader>
          <ViewTabs
            defaultKey={TransactionDetailsTab.GENERAL}
            activeTab={activeTab}
            onChange={(value) => setActiveTab(value)}
            tabs={[
              {
                key: TransactionDetailsTab.GENERAL,
                translateKey: 'General',
                visible: true,
                component: (
                  <TransactionDetailsBonusGeneralPage
                    transaction={transaction}
                    bonusTypes={bonusTypes}
                    bonusStatuses={bonusStatuses}
                    client={client}
                    permissions={permissions}
                    onSubmit={onSubmit}
                    setAuditLogModal={setAuditLogVisible}
                    setActiveTab={setActiveTab}
                    bonusLabels={bonusLabels}
                  />
                ),
              },
              {
                key: TransactionDetailsTab.FUNDING_SUMMARY,
                translateKey: <span>Funding Summary</span>,
                visible: true,
                component: <AggregateDataTable clientId={client?.id} />,
              },
              {
                key: TransactionDetailsTab.FUNDING_DETAIL,
                translateKey: <span>Funding Details</span>,
                visible: true,
                component: <PaymentProviderTransactionTable clientId={client?.id} />,
              },
              {
                key: TransactionDetailsTab.STATISTICS,
                translateKey: <span>Trading Account Statistics</span>,
                visible: true,
                component: client && <TransactionsStatisticOverview client={client} />,
              },
              {
                key: TransactionDetailsTab.LOTS_PROFIT_STATS,
                translateKey: <span>Lots/Profit Statistics</span>,
                visible: true,
                component: (
                  <TransactionDetailsLotsProfitStatistics
                    clientId={client?.id}
                    walletId={transactionWalletId}
                  />
                ),
              },
              {
                key: TransactionDetailsTab.LINKS,
                translateKey: (
                  <span>
                    {(!!data.linkedTo.length || !!data.linkedFrom.length) && (
                      <WarningOutlined style={{ color: 'red' }} />
                    )}{' '}
                    Links
                  </span>
                ),
                visible: true,
                component: <LinkedTransactionTable data={data} />,
              },
              {
                key: TransactionDetailsTab.NOTES,
                translateKey: <span>Notes</span>,
                visible: true,
                component: <TransactionDetailsNotesPage transaction={data} />,
              },
            ]}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default TransactionDetailsBonusPage
